import React, { useEffect, useState } from 'react';
import { AlertTriangle, ArrowRight, Ban, Loader2, Merge } from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '../../../components/ui/skeleton';
import { ErrorState } from '@/components/app';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { MergePreview, duplicatesApi } from '../../../api/duplicates';
import { UserSummaryCard, apiErrorMessage, getErrorCode, userTitle } from './shared';

// Коды причин, по которым backend запрещает склейку (merge-preview.blockReason и 409 code)
const BLOCK_REASON_TEXT: Record<string, string> = {
  DUPLICATE_HAS_PAYMENTS: 'У дубля есть оплаты или подписка. Решите вручную: возврат или продление.',
  SAME_USER: 'Выбран один и тот же аккаунт.',
  DUPLICATE_DELETED: 'Дубль удалён.',
  MAIN_DELETED: 'Основной аккаунт удалён.',
  DUPLICATE_MERGED: 'Дубль уже склеен.',
  MAIN_MERGED: 'Основной аккаунт сам склеен в другой — выберите тот, в который он склеен.',
  DUPLICATE_NO_TELEGRAM: 'У дубля нет Telegram — переносить нечего.',
  MAIN_HAS_OTHER_TELEGRAM: 'К основному аккаунту уже привязан другой Telegram.',
};

const reasonText = (code: string | null | undefined, fallback: string) =>
  (code && BLOCK_REASON_TEXT[code]) || fallback;

export interface MergeTarget {
  duplicateId: string;
  mainId: string;
}

interface MergeDialogProps {
  target: MergeTarget | null;
  onClose: () => void;
  onMerged: () => void;
}

/**
 * Окно подтверждения склейки дубля: грузит merge-preview, показывает сводку
 * последствий и только потом даёт нажать «Склеить».
 */
export function MergeDialog({ target, onClose, onMerged }: MergeDialogProps) {
  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // 409 DUPLICATE_HAS_PAYMENTS мог прийти уже на POST (оплата появилась после превью)
  const [mergeBlocked, setMergeBlocked] = useState<string | null>(null);

  useEffect(() => {
    if (!target) return;
    let cancelled = false;
    setPreview(null);
    setLoadError(null);
    setMergeBlocked(null);
    setLoading(true);
    duplicatesApi
      .mergePreview(target.duplicateId, target.mainId)
      .then((p) => {
        if (!cancelled) setPreview(p);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(apiErrorMessage(err, 'Не удалось загрузить сводку склейки'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [target]);

  const handleConfirm = async () => {
    if (!target || !preview?.canMerge) return;
    setSubmitting(true);
    try {
      await duplicatesApi.merge(target.duplicateId, target.mainId);
      toast.success('Дубль склеен с основным аккаунтом');
      onMerged();
      onClose();
    } catch (err: unknown) {
      const msg = reasonText(getErrorCode(err), apiErrorMessage(err, 'Не удалось выполнить склейку'));
      if (getErrorCode(err) === 'DUPLICATE_HAS_PAYMENTS' || (err as { response?: { status?: number } } | null)?.response?.status === 409) {
        setMergeBlocked(msg);
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const blockReason = mergeBlocked ?? (preview && !preview.canMerge ? reasonText(preview.blockReason, preview.blockReason || 'Склейка невозможна') : null);
  const canConfirm = !!preview?.canMerge && !mergeBlocked && !submitting;

  const tg = preview
    ? preview.willMove.telegramUsername
      ? `@${preview.willMove.telegramUsername}`
      : preview.willMove.telegramId
      ? `id ${preview.willMove.telegramId}`
      : null
    : null;

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="max-w-3xl p-0">
        <DialogHeader bordered>
          <DialogTitle>Склейка дубля</DialogTitle>
          <DialogDescription>Проверьте, что это один и тот же человек, и прочитайте, что произойдёт.</DialogDescription>
        </DialogHeader>

        <DialogBody>
          {loading && (
            <div className="flex flex-col gap-3" aria-busy="true" aria-label="Загрузка сводки">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Skeleton className="h-56" />
                <Skeleton className="h-56" />
              </div>
              <Skeleton className="h-24" />
            </div>
          )}

          {loadError && !loading && <ErrorState compact message={loadError} />}

          {preview && !loading && (
            <div className="flex flex-col gap-5">
              <div className="grid grid-cols-1 items-stretch gap-3 md:grid-cols-[1fr_auto_1fr]">
                <UserSummaryCard user={preview.duplicate} role="Дубль (Telegram)" tone="duplicate" />
                <div className="flex items-center justify-center">
                  <ArrowRight className="size-6 rotate-90 text-text-2 md:rotate-0" aria-hidden />
                </div>
                <UserSummaryCard user={preview.main} role="Основной" tone="main" />
              </div>

              <div className="flex flex-col gap-3 rounded-xl bg-surface-alt p-4 text-sm">
                <div>
                  <p className="font-semibold text-text">Основной «{userTitle(preview.main)}»</p>
                  <p className="text-text-2">
                    Получит вход через Telegram{tg ? <> <b className="text-text">{tg}</b></> : null}
                    {preview.willMove.phone ? (
                      <> и номер <b className="text-text">{preview.willMove.phone}</b></>
                    ) : null}
                    . Премиум, оплаты и прогресс основного не меняются.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-text">Дубль «{userTitle(preview.duplicate)}»</p>
                  <p className="text-text-2">
                    Станет «склеен»: вход в него закроется, активные сессии будут отозваны. Аккаунт не
                    удаляется — его прогресс останется в архиве на дубле и <b className="text-text">не переносится</b> на
                    основной.
                  </p>
                </div>
              </div>

              {blockReason ? (
                <div role="alert" className="flex gap-3 rounded-xl bg-neg-soft p-4 text-sm">
                  <Ban className="mt-0.5 size-5 shrink-0 text-neg" aria-hidden />
                  <div>
                    <p className="font-semibold text-neg">Склейка невозможна</p>
                    <p className="text-text">{blockReason}</p>
                    <p className="mt-1 text-text-2">
                      Решите вручную: например, если дубль платил — сделайте возврат или продлите
                      премиум на основном аккаунте.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex gap-3 rounded-xl bg-warn-soft p-4 text-sm">
                  <AlertTriangle className="mt-0.5 size-5 shrink-0 text-warn" aria-hidden />
                  <p className="text-text">
                    Действие <b>не отменяется кнопкой</b>. Если ошиблись — исправлять придётся вручную
                    в базе. Действие запишется в аудит-лог.
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogBody>

        <DialogFooter bordered>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Отмена
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={!canConfirm}>
            {submitting ? <Loader2 className="animate-spin" /> : <Merge />}
            Склеить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
